import { useEffect, useRef, useState } from 'react'

import { createCancelTokenSource, isCancel } from 'common/api'
import ConfirmationModal from 'common/components/confirmationModal'
import { DetailsPanelButton as Button } from 'common/components/detailsPanel'
import { errorToast, successToast } from 'common/components/toastNotification'
import { Deactivate } from 'common/icons'
import useUpdateProjectActionMutation from 'features/projects/mutations/useUpdateProjectActionMutation'

interface Props {
  accountId: string
  projectId: string
  isExpanded: boolean
}

const ReactivateButton = ({ isExpanded, accountId, projectId }: Props) => {
  const mutation = useUpdateProjectActionMutation()
  const sourceRef = useRef(createCancelTokenSource())

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  useEffect(() => {
    const source = sourceRef.current
    return () => source.cancel()
  }, [])

  const reactivate = async () => {
    const source = sourceRef.current

    try {
      await mutation.mutateAsync({
        action: 'deleted',
        value: false,
        accountId,
        projectId,
        source,
      })
      successToast('Project reactivated successfully')
      setIsModalOpen(false)
    } catch (error) {
      if (!isCancel(error)) {
        errorToast('Failed to reactivate project')
      }
    }
  }

  return (
    <>
      <Button
        isExpanded={isExpanded}
        data-cy="reactivate-project-button"
        data-testid="reactivate-project-button"
        title="Reactivate project"
        onClick={() => setIsModalOpen(true)}
        icon={<Deactivate isDefaultColor={false} className="text-red-400" />}
      />
      <ConfirmationModal
        data-cy="reactivate-project-confirmation-modal"
        data-testid="reactivate-project-confirmation-modal"
        title="Reactivate project?"
        confirmationText="This project will be reactivated."
        confirmButtonText="Yes, reactivate it!"
        open={isModalOpen}
        onConfirm={reactivate}
        onCancel={() => setIsModalOpen(false)}
        isLoading={mutation.isPending}
      />
    </>
  )
}

export default ReactivateButton
